import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import algoliasearch from 'algoliasearch/lite';
import { InstantSearch, Configure, connectHits } from 'react-instantsearch-dom';
import { hairline, rem, space } from '@gbi/ui';
import { RadioCircle } from '../InputFields/Radio';
import SectionLoader from '../Loader/SectionLoader';
import BasicModalBody from '../BasicModal/BasicModalBody';
import Anchor from '../Anchor/Anchor';
import ModalButtons from '../Modal/ModalButtons';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import tokens from '../../assets/styles/tokens';
import config from '../../config';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { getCurrentAccountUid } from '../../store/selectors/accountSelectors';
import { fetchProductsWithPrices } from '../../store/actions/products/productActions';
import { selectProductPrices } from '../../store/selectors/productsSelector';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { PRODUCT_PRICES_LOADING } from '../../store/actions/products/productActionConstants';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import { removeFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  GTT_TARGET_PRICE,
  GTT_TARGET_QUANTITY,
} from '../../constants/sessionStorage';
import useGtmHook from '../../util/hooks/useGtmHook';
import {
  ENTRY_POINTS,
  trackTriggerOrderStarted,
  trackTriggerOrderProductSelected,
} from './Common/gtmEventHelper';
import {
  METAL_FILTERS,
  mergeHitsWithPrices,
  filterProducts,
  getAvailableWeights,
  buildProductImageSrc,
} from './Common/productFilterHelpers';
import SelectWeightDrawer from './SelectWeightDrawer';
import {
  GUTTER,
  FilterRow,
  MetalChips,
  MetalChip,
  MetalChipCheck,
  SearchToggle,
  SearchInputWrapper,
  SearchInput,
  SearchClearButton,
  SearchClearIcon,
  SearchIcon,
  WeightSelector,
  WeightLabel,
  WeightField,
  WeightFieldChange,
  WeightFieldArrow,
  ProductSelectionDivider,
  InstructionText,
  ScrollArea,
  List,
  EmptyState,
  EmptyStateIcon,
  EmptyStatePrimaryText,
  EmptyStateSecondaryText,
  FullWidthApply,
  ProductRow,
  ProductRadio,
  ProductThumb,
  ProductMeta,
  ProductNameText,
  ProductPriceText,
  ProductWeightText,
} from './Common';

const { apiKey, apiSecret, apiBuyIndex } = config.algoliaSearch;
// Module-level so the client is created once, not on every render.
const searchClient = algoliasearch(apiKey, apiSecret);

const METAL_CHIPS = [
  { value: METAL_FILTERS.ALL, labelKey: 'common.all' },
  { value: METAL_FILTERS.GOLD, labelKey: 'common.gold' },
  { value: METAL_FILTERS.SILVER, labelKey: 'common.silver' },
];

// Fill the modal body's height and clip its own overflow so the inner
// ScrollArea is the single scroll region: the fixed filter header sits at the
// top, the list scrolls, and the Continue footer stays pinned at the bottom.
//
// The shared BasicModal shell is height:auto with a min-height of 650px (see
// ModalElement/getModalSize). Because it is auto-height, the long product list
// would otherwise stretch the modal all the way to its max-height (the
// viewport) — making this screen much taller than the AlertEntry screen, which
// simply rests at the shell's 650px min-height. Pin the body to a definite
// desktop height (the shell height minus the ~50px header) so the list scrolls
// inside the ScrollArea and this modal matches the AlertEntry size. Expressed
// in rem so it tracks the shell's rem-based min-height across breakpoints.
//
// Padding follows the frame at every width: 12px under the header, the GUTTER
// at the sides and bottom (BasicModalBody's own padding shrinks with
// --legacy-scale).
const ModalBody = styled(BasicModalBody)`
  min-height: 0;
  height: calc(${pxToRem(650)} - ${pxToRem(50)});
  overflow: hidden;
  padding: ${rem(space[12])} ${GUTTER} ${GUTTER};

  // Mobile: the modal is full-screen (ModalElement is height:100%), so fill it
  // and let the body flex instead of pinning a fixed height.
  ${mediaBelow(variables.breakpoints.bpMd)} {
    height: 100%;
  }
`;

// Continue footer: 16px under the list, a full-bleed rule, then 16px above
// the button, as in the frame.
const ContinueFooter = styled(FullWidthApply)`
  margin: ${rem(space[16])} calc(-1 * ${GUTTER}) 0;
  padding: ${rem(space[16])} ${GUTTER} 0;
  border-top: ${hairline} solid ${tokens.color.border.subtle};
`;

// A single memoized product row. React.memo keeps unaffected rows from
// re-rendering when only the selection or an unrelated filter changes.
const ProductRowItem = React.memo(
  ({ product, selected, onSelect, imageCode, rootUrl }) => {
    const code = product.ProductCode || product.Code;
    const price = product.Locations?.[0]?.PricePerUnit ?? product.ProductPrice;

    return (
      <li>
        <ProductRow
          isSelected={selected}
          onClick={() => onSelect(product)}
          data-cy={`gtt-product-row-${code}`}
        >
          <ProductRadio>
            <RadioCircle
              selected={selected}
              circleWidth="16px"
              circleHeight="16px"
              circleBorderRadius="50%"
              circleBorderWidth="2px"
            />
          </ProductRadio>
          <ProductThumb
            src={encodeURI(buildProductImageSrc(imageCode, rootUrl, code))}
            alt={product.Caption}
          />
          <ProductMeta>
            <ProductNameText>{product.ShortCaption}</ProductNameText>
            <ProductPriceText>{formatMoneyNumeral(price)}</ProductPriceText>
          </ProductMeta>
          <ProductWeightText>{product.WeightCaption}</ProductWeightText>
        </ProductRow>
      </li>
    );
  },
);

ProductRowItem.displayName = 'ProductRowItem';

ProductRowItem.propTypes = {
  product: PropTypes.shape({
    ProductCode: PropTypes.string,
    Code: PropTypes.string,
    ShortCaption: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
    ProductPrice: PropTypes.number,
    Locations: PropTypes.arrayOf(
      PropTypes.shape({ PricePerUnit: PropTypes.number }),
    ),
  }).isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  imageCode: PropTypes.string,
  rootUrl: PropTypes.string,
};

// Inner list, connected to Algolia hits. Merges hit metadata with prices,
// applies the client-side filters, and reports the catalog's distinct weights
// up to the parent so the weight drawer stays in sync with real inventory.
const ProductHits = connectHits(
  ({
    hits,
    productPrices,
    metalFilter,
    weightFilters,
    searchText,
    selectedCode,
    onSelect,
    imageCode,
    rootUrl,
    onWeightsAvailable,
    emptyTitle,
    emptySub,
  }) => {
    const merged = useMemo(() => mergeHitsWithPrices(hits, productPrices), [
      hits,
      productPrices,
    ]);

    useEffect(() => {
      onWeightsAvailable(getAvailableWeights(merged));
    }, [merged, onWeightsAvailable]);

    const filtered = useMemo(
      () => filterProducts(merged, { metalFilter, weightFilters, searchText }),
      [merged, metalFilter, weightFilters, searchText],
    );

    if (!filtered.length) {
      return (
        <EmptyState>
          <EmptyStateIcon />
          <EmptyStatePrimaryText>{emptyTitle}</EmptyStatePrimaryText>
          <EmptyStateSecondaryText>{emptySub}</EmptyStateSecondaryText>
        </EmptyState>
      );
    }

    return (
      <List>
        {filtered.map((product) => {
          const code = product.ProductCode || product.Code;
          return (
            <ProductRowItem
              key={code}
              product={product}
              selected={code === selectedCode}
              onSelect={onSelect}
              imageCode={imageCode}
              rootUrl={rootUrl}
            />
          );
        })}
      </List>
    );
  },
);

const TargetPriceProductSelection = ({
  wizardData,
  setWizardTitle,
  setGoBackHandler,
  closeModal,
  setWizardData,
  handleNext,
  isLastStep,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ecommerceGtmEvent } = useGtmHook();
  const { RootUrl, SupportPhone } = useSelector(selectSettings);
  const accountUid = useSelector(getCurrentAccountUid);
  const productPrices = useSelector(selectProductPrices);
  const isLoading = useSelector(
    selectIsLoadingByActionType(PRODUCT_PRICES_LOADING),
  );

  const [metalFilter, setMetalFilter] = useState(METAL_FILTERS.ALL);
  const [weightFilters, setWeightFilters] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isWeightDrawerOpen, setIsWeightDrawerOpen] = useState(false);
  const [availableWeights, setAvailableWeights] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // First step: set the header title and make the back chevron close the modal
  // (there is no previous step to go back to). Fire the "started" GTM event and
  // load the buy-side product prices once on mount.
  useEffect(() => {
    setWizardTitle(t('gtt.common.buyAtTargetPrice'));
    setGoBackHandler(() => closeModal);
    // Reaching the product-selection step is a fresh target-price journey, so
    // drop any target price/quantity left over from a previous session. The
    // Buy round trip re-enters GTT on the order-entry step (never here), so
    // its retained values are preserved.
    removeFromSessionStorage(GTT_TARGET_PRICE);
    removeFromSessionStorage(GTT_TARGET_QUANTITY);
    trackTriggerOrderStarted({
      ecommerceGtmEvent,
      entryPoint: ENTRY_POINTS.HEADER,
    });
    return () => setGoBackHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchProductsWithPrices({ accountUid, side: 'Buy' }));
    }
  }, [accountUid, dispatch]);

  const handleWeightsAvailable = useCallback((weights) => {
    setAvailableWeights((prev) =>
      prev.length === weights.length && prev.every((w, i) => w === weights[i])
        ? prev
        : weights,
    );
  }, []);

  const handleSelectProduct = useCallback(
    (product) => setSelectedProduct(product),
    [],
  );

  const toggleSearch = () => {
    setIsSearchOpen((open) => {
      if (open) {
        setSearchText('');
      }
      return !open;
    });
  };

  const handleContinue = () => {
    const previousProduct = wizardData?.selectedProduct;
    const previousCode = previousProduct?.ProductCode || previousProduct?.Code;
    const newCode = selectedProduct?.ProductCode || selectedProduct?.Code;
    const isDifferentProduct = newCode !== previousCode;
    // The target price, quantity, and vault entered on a previous visit to
    // Order Entry all belong to the old product (price is set relative to its
    // market price, the vault's SymbolCode is specific to it, and quantity was
    // sized against it) — meaningless for a newly-picked product, so drop them
    // all. Same product re-confirmed → leave everything alone.
    const orderValues = isDifferentProduct
      ? { CashAmount: '', QuantityAmount: '' }
      : wizardData?.orderValues;
    const selectedVault = isDifferentProduct
      ? undefined
      : wizardData?.selectedVault;

    setWizardData({ selectedProduct, orderValues, selectedVault });
    trackTriggerOrderProductSelected({
      ecommerceGtmEvent,
      productCode: selectedProduct?.ProductCode || selectedProduct?.Code,
      productName: selectedProduct?.Caption,
    });
    // The succeeding screens (Order Entry / Review / Confirmation) aren't built
    // yet, so don't advance off the last defined step — that would blank the
    // modal. Once they exist this screen is no longer last and advances.
    if (!isLastStep) {
      handleNext();
    }
  };

  const selectedCode = selectedProduct?.ProductCode || selectedProduct?.Code;
  const weightFieldLabel =
    weightFilters.length === 0
      ? t('gtt.triggerOrders.weight.allWeights')
      : weightFilters.join(', ');

  return (
    <>
      <SectionLoader isLoading={isLoading}>
        <ModalBody>
          <FilterRow>
            {isSearchOpen ? (
              <SearchInputWrapper>
                <SearchInput
                  type="text"
                  autoFocus
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder={t(
                    'gtt.triggerOrders.productSelection.searchPlaceholder',
                  )}
                  data-cy="gtt-product-search"
                />
                <SearchClearButton
                  type="button"
                  onClick={() =>
                    searchText ? setSearchText('') : toggleSearch()
                  }
                  aria-label={
                    searchText ? t('common.clear') : t('common.close')
                  }
                  data-cy="gtt-product-search-clear"
                >
                  <SearchClearIcon />
                </SearchClearButton>
              </SearchInputWrapper>
            ) : (
              <MetalChips>
                {METAL_CHIPS.map(({ value, labelKey }) => {
                  const isSelected = metalFilter === value;
                  return (
                    <MetalChip
                      key={value}
                      type="button"
                      isSelected={isSelected}
                      onClick={() => setMetalFilter(value)}
                      data-cy={`gtt-metal-filter-${value}`}
                    >
                      {t(labelKey)}
                      {isSelected && <MetalChipCheck />}
                    </MetalChip>
                  );
                })}
              </MetalChips>
            )}
            <SearchToggle
              type="button"
              onClick={toggleSearch}
              data-cy="gtt-search-toggle"
              aria-label={t('common.search')}
            >
              <SearchIcon />
            </SearchToggle>
          </FilterRow>

          <WeightSelector>
            <WeightLabel>{t('gtt.triggerOrders.weight.label')}</WeightLabel>
            <WeightField
              type="button"
              onClick={() => setIsWeightDrawerOpen(true)}
              data-cy="gtt-weight-change"
            >
              {weightFieldLabel}
              <WeightFieldChange>
                {t('gtt.triggerOrders.weight.change')}
                <WeightFieldArrow />
              </WeightFieldChange>
            </WeightField>
          </WeightSelector>

          <ProductSelectionDivider />

          <InstructionText>
            {t('gtt.triggerOrders.productSelection.instruction')}
          </InstructionText>

          <ScrollArea>
            <InstantSearch indexName={apiBuyIndex} searchClient={searchClient}>
              <Configure hitsPerPage={200} />
              <ProductHits
                productPrices={productPrices}
                metalFilter={metalFilter}
                weightFilters={weightFilters}
                searchText={searchText}
                selectedCode={selectedCode}
                onSelect={handleSelectProduct}
                onWeightsAvailable={handleWeightsAvailable}
                imageCode={config.algoliaSearch.imageCode}
                rootUrl={RootUrl}
                emptyTitle={t('gtt.triggerOrders.productSelection.noProducts')}
                emptySub={
                  SupportPhone ? (
                    <>
                      {t('gtt.triggerOrders.productSelection.needHelp')}{' '}
                      <Anchor
                        type="telephone"
                        value={SupportPhone}
                        text={SupportPhone}
                        bold
                        data-cy="gtt-empty-state-support-phone"
                      />
                    </>
                  ) : null
                }
              />
            </InstantSearch>
          </ScrollArea>

          <ContinueFooter>
            <ModalButtons
              showOnlyPrimary
              primaryButtonProps={{
                label: t('common.continue'),
                onClick: handleContinue,
                disabled: !selectedProduct,
                textTransform: 'capitalize',
                'data-cy': 'gtt-continue',
              }}
            />
          </ContinueFooter>
        </ModalBody>
      </SectionLoader>

      {/* Rendered outside SectionLoader so its position:absolute anchors to the
          non-scrolling ModalElement (SectionLoader's LoaderContainer is
          position:relative and scrolls with the body), pinning the drawer to
          the bottom of the modal. */}
      <SelectWeightDrawer
        isOpen={isWeightDrawerOpen}
        onClose={() => setIsWeightDrawerOpen(false)}
        availableWeights={availableWeights}
        selectedWeights={weightFilters}
        onApply={setWeightFilters}
      />
    </>
  );
};

TargetPriceProductSelection.propTypes = {
  wizardData: PropTypes.shape({
    selectedProduct: PropTypes.shape({
      ProductCode: PropTypes.string,
      Code: PropTypes.string,
    }),
    orderValues: PropTypes.shape({
      CashAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      QuantityAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      SymbolCode: PropTypes.string,
    }),
  }),
  setWizardTitle: PropTypes.func,
  setGoBackHandler: PropTypes.func,
  closeModal: PropTypes.func,
  setWizardData: PropTypes.func,
  handleNext: PropTypes.func,
  isLastStep: PropTypes.bool,
};

export default TargetPriceProductSelection;
